/**
 * ElevenLabs v4 für die Meditation: was die Seite zeigt und was die Hörprobe
 * sagt. (07.10.2026)
 *
 * Martin: *„In erster Linie möchte ich alternativ NUR FÜR DIE MEDITATION die
 * ElevenLabs V4 haben."* Die Wahl steht unter „Stimme der Meditation"
 * (settings-view.ts, meditationElevenKarte), nur für Konto 1. Die
 * Stimmen-Wahl des Vorlesens bleibt, wie sie ist.
 *
 * Feld, Stimmen und Konto-Regel stehen EINMAL in shared/meditations-stimme.ts
 * (`meditationElevenStimme`, MEDITATION_ELEVEN_STIMMEN), das Modell am Server
 * (server/eleven-v4.ts, MEDITATION_ELEVEN_MODELL = Eleven v4), was es kostet
 * in shared/meditation-kosten.ts. Die Seite nennt darum kein Modell, sie
 * bittet um eine Probe „für die Meditation".
 *
 * ⚠️ Ob eine Probe wirklich mit v4 gesprochen wurde, sagt nur der Server, in
 *    der Kopfzeile `X-Eleven-Modell`. Ohne sie meldet die Seite nie „v4" —
 *    sonst stünde „v4" da, während ein Server ohne v4 still das alte Modell
 *    spricht (deuteElevenProbe).
 */

/**
 * Der Satz der Hörprobe und der Zweck stehen EINMAL in shared/eleven-regeln.ts: der Server spricht bei diesem Zweck
 * nur diesen Satz (nie freien Text aus dem Körper), die Seite schickt ihn mit. Hier nur durchgereicht.
 */
export { MEDITATIONS_PROBE } from "../../shared/eleven-regeln.js";
import { MEDITATIONS_PROBE_ZWECK } from "../../shared/eleven-regeln.js";

/** Was die Probe-Anfrage dem Server sagt: diese Probe ist für die Meditation (er wählt das Modell). */
export const PROBE_ZWECK = MEDITATIONS_PROBE_ZWECK;

export const ELEVEN_TITEL = "ElevenLabs v4 für die Meditation";

export const ELEVEN_AUS = "Aus — es liest Soniox";

/** Eine gespeicherte Stimme, die nicht mehr in der Liste steht (z. B. eine gelöschte eigene). */
export const ELEVEN_VERWAIST = "Gespeicherte Stimme (nicht mehr in deiner Liste)";

export const ELEVEN_VERWAIST_HINWEIS =
  "Diese Stimme steht nicht mehr in deiner Liste. Die Meditation liest trotzdem weiter mit ihr und " +
  "kostet ElevenLabs-Credits. Wähle „Aus“, damit wieder Soniox liest.";

export const ELEVEN_MEHR_FRAGE = "Was ändert sich mit ElevenLabs?";

export const ELEVEN_MEHR_ANTWORT =
  "Nur die Morgenmeditation liest dann mit der gewählten ElevenLabs-Stimme, gesprochen mit " +
  "Eleven v4. Antworten, Vorlesen und der Anruf bleiben bei ihren Stimmen. Bezahlt wird über " +
  "deine ElevenLabs-Credits, nicht über Soniox — jeden Morgen eine ganze Meditation, also mehrere " +
  "Tausend Zeichen. Spricht ElevenLabs nicht (keine Credits, Modell nicht verfügbar), liest die " +
  "Soniox-Stimme darüber. Die Wahl gilt ab der nächsten Meditation; eine schon fertige bleibt, wie " +
  "sie ist. Mikrofon und Spracherkennung bleiben bei Soniox.";

/** Die Zeile nach dem Speichern. */
export function elevenGespeichert(name: string | null): string {
  return name
    ? `Ab der nächsten Meditation liest ${name} mit ElevenLabs v4.`
    : "Die Meditation liest wieder mit Soniox.";
}

/** Was die Seite von einer Antwort des Servers braucht — ohne Response-Objekt testbar. */
export interface ProbeAntwort {
  ok: boolean;
  status: number;
  kopf: (name: string) => string | null;
  /** Bei Fehlern: der Text der Antwort (JSON `{ error, detail }`), soweit lesbar. */
  text?: string;
}

export type ProbeDeutung =
  | { spielt: true; meldung: string | null; art?: "hinweis" }
  | { spielt: false; meldung: string; art: "fehler" };

type Grund = "credits" | "modell" | "stimme" | null;

/**
 * Warum ElevenLabs nicht gesprochen hat. Zuerst die Fehlerart im Körper der
 * Antwort (`{ art }`, ElevenFehlerArt aus server/eleven-v4.ts) — der Server
 * antwortet bei einem Fehler von ElevenLabs mit 502 und nennt die Art dort,
 * nie mit dem Status des fremden Dienstes. Ohne Art (ein älterer Server):
 * Status und Text, wie ElevenLabs sie liefert (401 „quota_exceeded", 402).
 */
function grundAus(status: number, text: string): Grund {
  try {
    const art = (JSON.parse(text) as { art?: unknown } | null)?.art;
    if (art === "guthaben") return "credits";
    if (art === "modell" || art === "stimme") return art;
    if (typeof art === "string") return null;
  } catch {
    /* kein JSON — dann Status und Text */
  }
  if (status === 402) return "credits";
  if ((status === 401 || status === 429) && /quota|credit|insufficient|payment/i.test(text)) {
    return "credits";
  }
  if (status >= 400 && /model/i.test(text)) return "modell";
  return null;
}

/** Wer statt ElevenLabs gesprochen hat — `null`, wenn es ElevenLabs selbst war. */
function ersatzAus(a: ProbeAntwort): string | null {
  const anbieter = (a.kopf("X-Voice-Provider") ?? "").toLowerCase();
  const ersatz = (a.kopf("X-Voice-Fallback") ?? "").toLowerCase();
  if (!ersatz && (!anbieter || anbieter.startsWith("elevenlabs"))) return null;
  const quelle = ersatz || anbieter;
  if (quelle.startsWith("soniox")) return "Soniox";
  if (/^(google|cloud-tts|gemini)/.test(quelle)) return "eine Google-Stimme";
  return "eine andere ElevenLabs-Stimme";
}

const DANN_SONIOX = "die Meditation liest dann mit der Soniox-Stimme darüber.";

/**
 * Was die Hörprobe einer ElevenLabs-Stimme dem Menschen sagt. Nie stumm:
 * spielt nichts, steht der Grund da; spielt jemand anderes, steht da, wer.
 *
 * Kopfzeilen des Servers: `X-Eleven-Modell` (womit ElevenLabs gesprochen hat,
 * „eleven_v4" oder „v4"), `X-Voice-Fallback` / `X-Voice-Provider` (wer statt
 * dessen sprach — nur auf dem allgemeinen Weg; die Probe für die Meditation
 * spricht nie mit Ersatz). Ein Fehler ohne Ton: Status (heute 502) und JSON
 * `{ error, art, detail }`.
 */
export function deuteElevenProbe(a: ProbeAntwort): ProbeDeutung {
  if (!a.ok) {
    const grund = grundAus(a.status, a.text ?? "");
    if (grund === "credits") {
      return { spielt: false, art: "fehler", meldung: `ElevenLabs hat gerade keine Credits — ${DANN_SONIOX}` };
    }
    if (grund === "modell") {
      return { spielt: false, art: "fehler", meldung: `v4 ist gerade nicht verfügbar — ${DANN_SONIOX}` };
    }
    if (grund === "stimme") {
      return {
        spielt: false,
        art: "fehler",
        meldung: "Diese Stimme ist für v4 noch nicht freigeschaltet — es liest Soniox.",
      };
    }
    return {
      spielt: false,
      art: "fehler",
      meldung: "ElevenLabs spricht gerade nicht — die Probe kann nicht spielen.",
    };
  }
  const ersatz = ersatzAus(a);
  if (ersatz) {
    return { spielt: true, art: "hinweis", meldung: `ElevenLabs hat diese Probe nicht gesprochen — du hörst ${ersatz}.` };
  }
  const modell = (a.kopf("X-Eleven-Modell") ?? "").trim().toLowerCase();
  if (/^(eleven_)?v4/.test(modell)) return { spielt: true, meldung: "Gesprochen mit ElevenLabs v4." };
  if (!modell) {
    return {
      spielt: true,
      art: "hinweis",
      meldung: "Ob v4 gesprochen hat, bestätigt der Server nicht — vielleicht war es das bisherige Modell.",
    };
  }
  return {
    spielt: true,
    art: "hinweis",
    meldung: "v4 ist gerade nicht verfügbar — die Probe kommt vom bisherigen Modell.",
  };
}
